/**
 * Sign Shop Catalog API — v2 contract (shadow: intrlock-shadow-sign-shop-api-v2)
 *
 * Rewritten §28.518.99.49.d to point at the new v2 orchestrator on shadow.
 * All calls flow through connector-api on the backend (no direct vendor calls).
 *
 * v2 endpoint base:
 *   https://zlj5y4dnz6.execute-api.us-east-1.amazonaws.com/sign-shop
 *
 * Route surface (per §28.518.99.49.c spec):
 *   GET  /sign-shop/products
 *   GET  /sign-shop/products/:id
 *   GET  /sign-shop/templates
 *   GET  /sign-shop/templates/:id
 *   GET  /sign-shop/designs                 (org-scoped, JWT-gated)
 *   POST /sign-shop/designs
 *   PUT  /sign-shop/designs/:id
 *   DELETE /sign-shop/designs/:id
 *   POST /sign-shop/preview                 (returns mockup URLs)
 *   POST /sign-shop/order-init              (creates Stripe Checkout Session; returns hosted URL)
 *   GET  /sign-shop/orders                  (org-scoped)
 *   GET  /sign-shop/orders/:id
 *
 * Admin (super-admin JWT):
 *   GET  /admin/sign-shop/pending
 *   POST /admin/sign-shop/orders/:id/approve
 *   POST /admin/sign-shop/orders/:id/reject
 *   GET/PUT /admin/sign-shop/pricing-rules
 *   GET/PUT /admin/sign-shop/approval-rules
 *   GET/PUT /admin/sign-shop/kill-switch
 *   GET  /admin/sign-shop/moderation
 *   POST /admin/sign-shop/blueprint-sync    (manual trigger)
 *   GET  /admin/sign-shop/blueprint-sync/status
 *
 * On CES (app/admin.intrlock.io) the legacy /stripe/sign-shop API stays live until Phase 3 cutover.
 */
import { apiOrigin } from '../utils/surface';

// ── Base URL routing ────────────────────────────────────────────────────────

const V2_BASE = `${apiOrigin()}/sign-shop`;
const V2_ADMIN_BASE = `${apiOrigin()}/sign-shop`;

const authHeaders = (): Record<string, string> => {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('intrlock_token')) || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const jsonHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...authHeaders(),
});

async function handle(res: Response): Promise<any> {
  if (!res.ok) {
    const body: any = await res.json().catch(() => ({}));
    const err = new Error(body?.error || body?.message || `${res.status} ${res.statusText}`);
    (err as any).status = res.status;
    (err as any).body = body;
    throw err;
  }
  return res.json();
}

// ── Types (v2 response shapes) ──────────────────────────────────────────────

export interface SignProduct {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string; // signs | stickers | decals | banners | apparel | accessories
  supplier: string; // printify | vistaprint | manual
  blueprint_id?: number | null;
  print_provider_id?: number | null;
  supplier_product_id?: string;
  supplier_url?: string;
  base_price_cents: number;
  image_url: string;
  thumbnail_url: string;
  active: boolean;
  sort_order: number;
  // Design canvas constraints (from Printify blueprint / manual spec)
  canvas_width_px: number;
  canvas_height_px: number;
  print_area_width_in: number;
  print_area_height_in: number;
  bleed_px: number;             // extra pixels around edge that WILL print but may be trimmed
  safe_zone_px: number;         // inset from edge where no critical content should be placed
  aspect_ratio: number;         // canvas_width_px / canvas_height_px
  min_qty: number;
  max_qty: number;
  production_days: number;
  variants?: SignVariant[];
}

export interface SignVariant {
  id: number;
  title: string;
  price_cents: number;
  is_default: boolean;
  in_stock: boolean;
}

export interface SignTemplate {
  id: string;
  product_id: string;
  name: string;
  description: string;
  category: string;
  compositor_spec: CompositorSpec;
  thumbnail_url: string;
  property_fields: string[]; // fields to auto-fill from property data
  active: boolean;
  sort_order: number;
}

export interface SignDesign {
  id: string;
  org_id: string;
  user_id: string;
  product_id: string;
  name: string;
  compositor_spec: CompositorSpec;
  thumbnail_url?: string;
  is_draft: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Compositor spec — the shape SignDesigner produces and the render Lambda consumes.
 * Backend render Lambda rasterizes this to a PNG at product.canvas_width_px × canvas_height_px.
 */
export interface CompositorSpec {
  version: 1;
  canvas: {
    width_px: number;
    height_px: number;
    background: string; // hex color
  };
  objects: CompositorObject[];
}

export type CompositorObject =
  | CompositorText
  | CompositorImage
  | CompositorShape
  | CompositorQR;

interface CompositorBase {
  id: string;
  type: string;
  left: number;
  top: number;
  width: number;
  height: number;
  angle: number;
  opacity: number;
  flipX: boolean;
  flipY: boolean;
  zIndex: number;
}

export interface CompositorText extends CompositorBase {
  type: 'text';
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  underline: boolean;
  textAlign: 'left' | 'center' | 'right';
  fill: string;
  stroke?: string;
  strokeWidth?: number;
}

export interface CompositorImage extends CompositorBase {
  type: 'image';
  src: string; // data URL or https URL
}

export interface CompositorShape extends CompositorBase {
  type: 'rect' | 'circle' | 'triangle' | 'polygon' | 'line' | 'group';
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  points?: { x: number; y: number }[]; // for polygon/line
}

export interface CompositorQR extends CompositorBase {
  type: 'qr';
  target: string; // URL encoded into the QR
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
}

// ── Products ────────────────────────────────────────────────────────────────

export async function getSignProducts(params?: {
  category?: string;
  active?: boolean;
}): Promise<SignProduct[]> {
  const qs = new URLSearchParams();
  if (params?.category) qs.set('category', params.category);
  if (params?.active !== undefined) qs.set('active', String(params.active));
  const url = `${V2_BASE}/products${qs.toString() ? '?' + qs : ''}`;
  const res = await fetch(url, { headers: authHeaders() });
  const body = await handle(res);
  return Array.isArray(body) ? body : body.products;
}

export async function getSignProduct(id: string): Promise<SignProduct> {
  const res = await fetch(`${V2_BASE}/products/${encodeURIComponent(id)}`, { headers: authHeaders() });
  return handle(res);
}

// ── Templates ───────────────────────────────────────────────────────────────

export async function getSignTemplates(productId?: string): Promise<SignTemplate[]> {
  const qs = productId ? `?product_id=${encodeURIComponent(productId)}` : '';
  const res = await fetch(`${V2_BASE}/templates${qs}`, { headers: authHeaders() });
  const body = await handle(res);
  return Array.isArray(body) ? body : body.templates;
}

export async function getSignTemplate(id: string): Promise<SignTemplate> {
  const res = await fetch(`${V2_BASE}/templates/${encodeURIComponent(id)}`, { headers: authHeaders() });
  return handle(res);
}

// ── Designs (My Designs, org-scoped) ────────────────────────────────────────

export async function listMyDesigns(params?: {
  product_id?: string;
  is_draft?: boolean;
}): Promise<SignDesign[]> {
  const qs = new URLSearchParams();
  if (params?.product_id) qs.set('product_id', params.product_id);
  if (params?.is_draft !== undefined) qs.set('is_draft', String(params.is_draft));
  const url = `${V2_BASE}/designs${qs.toString() ? '?' + qs : ''}`;
  const res = await fetch(url, { headers: authHeaders() });
  const body = await handle(res);
  return Array.isArray(body) ? body : body.designs;
}

export async function createDesign(data: {
  product_id: string;
  name: string;
  compositor_spec: CompositorSpec;
  is_draft?: boolean;
  thumbnail_url?: string;
}): Promise<SignDesign> {
  const res = await fetch(`${V2_BASE}/designs`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(data),
  });
  return handle(res);
}

export async function updateDesign(
  id: string,
  data: Partial<Pick<SignDesign, 'name' | 'compositor_spec' | 'is_draft' | 'thumbnail_url'>>,
): Promise<SignDesign> {
  const res = await fetch(`${V2_BASE}/designs/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(data),
  });
  return handle(res);
}

export async function deleteDesign(id: string): Promise<void> {
  const res = await fetch(`${V2_BASE}/designs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) {
    await handle(res);
  }
}

// ── Preview (live mockup) ───────────────────────────────────────────────────

export interface PreviewResponse {
  preview_id: string;
  mockup_urls: string[];
  mockups: Array<{ url: string; label: string; is_default: boolean; camera: string }>;
  cached: boolean;
}

export async function renderPreview(data: {
  product_id: string;
  variant_id?: number;
  compositor_spec: CompositorSpec;
}): Promise<PreviewResponse> {
  const res = await fetch(`${V2_BASE}/preview`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(data),
  });
  return handle(res);
}

// ── Admin: pricing / approval rules / kill switch / moderation / blueprints ─

export interface PricingRule {
  id: string;
  category: string;
  markup_pct: number;
  min_margin_cents: number;
  active: boolean;
  updated_at?: string;
}

export interface ApprovalRule {
  id: string;
  rule_type: string; // "min_margin" | "risk_score" | "keyword" | "manual"
  threshold: number | string;
  action: 'auto_approve' | 'auto_reject' | 'require_review';
  active: boolean;
}

export interface KillSwitchState {
  enabled: boolean;
  reason?: string;
  updated_at?: string;
  updated_by?: string;
}

export interface BlueprintSyncStatus {
  last_run_at: string | null;
  last_status: 'idle' | 'running' | 'success' | 'failed';
  products_synced: number;
  errors: string[];
  running: boolean;
}

export const adminSignShopApi = {
  listPending: (): Promise<{ orders: any[] }> =>
    fetch(`${V2_ADMIN_BASE}/pending`, { headers: authHeaders() }).then(r => handle(r)),

  approve: (orderId: string, notes?: string): Promise<any> =>
    fetch(`${V2_ADMIN_BASE}/orders/${encodeURIComponent(orderId)}/approve`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ notes: notes || '' }),
    }).then(r => handle(r)),

  reject: (orderId: string, reason: string): Promise<any> =>
    fetch(`${V2_ADMIN_BASE}/orders/${encodeURIComponent(orderId)}/reject`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ reason }),
    }).then(r => handle(r)),

  getPricingRules: (): Promise<PricingRule[]> =>
    fetch(`${V2_ADMIN_BASE}/pricing-rules`, { headers: authHeaders() }).then(r => handle(r)),

  putPricingRules: (rules: PricingRule[]): Promise<{ ok: true; rules: PricingRule[] }> =>
    fetch(`${V2_ADMIN_BASE}/pricing-rules`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ rules }),
    }).then(r => handle(r)),

  getApprovalRules: (): Promise<ApprovalRule[]> =>
    fetch(`${V2_ADMIN_BASE}/approval-rules`, { headers: authHeaders() }).then(r => handle(r)),

  putApprovalRules: (rules: ApprovalRule[]): Promise<{ ok: true; rules: ApprovalRule[] }> =>
    fetch(`${V2_ADMIN_BASE}/approval-rules`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ rules }),
    }).then(r => handle(r)),

  getKillSwitch: (): Promise<KillSwitchState> =>
    fetch(`${V2_ADMIN_BASE}/kill-switch`, { headers: authHeaders() }).then(r => handle(r)),

  setKillSwitch: (enabled: boolean, reason?: string): Promise<KillSwitchState> =>
    fetch(`${V2_ADMIN_BASE}/kill-switch`, {
      method: 'PUT',
      headers: jsonHeaders(),
      body: JSON.stringify({ enabled, reason: reason || '' }),
    }).then(r => handle(r)),

  getModerationQueue: (): Promise<{ items: any[] }> =>
    fetch(`${V2_ADMIN_BASE}/moderation`, { headers: authHeaders() }).then(r => handle(r)),

  getBlueprintSyncStatus: (): Promise<BlueprintSyncStatus> =>
    fetch(`${V2_ADMIN_BASE}/blueprint-sync/status`, { headers: authHeaders() }).then(r => handle(r)),

  triggerBlueprintSync: (): Promise<{ ok: true; run_id: string }> =>
    fetch(`${V2_ADMIN_BASE}/blueprint-sync`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: '{}',
    }).then(r => handle(r)),
};

// ── Design-constraint helpers (client-side clamp before rendering) ──────────

export interface CanvasBounds {
  width_px: number;
  height_px: number;
  bleed_px: number;
  safe_zone_px: number;
  aspect_ratio: number;
}

export function productToCanvasBounds(p: SignProduct): CanvasBounds {
  return {
    width_px: p.canvas_width_px,
    height_px: p.canvas_height_px,
    bleed_px: p.bleed_px ?? 0,
    safe_zone_px: p.safe_zone_px ?? 0,
    aspect_ratio: p.aspect_ratio ?? (p.canvas_width_px / p.canvas_height_px),
  };
}

// ── Backward-compat shims (legacy ProductConfigurator, seed importer, etc.) ─
// These preserve the pre-v2 type names and helpers so we don't need to rewrite
// callers in the same landing. Remove after all consumers are migrated to v2.

export interface SignOption {
  id: number;
  product_id: number | string;
  option_type: string;
  option_value: string;
  option_label: string;
  price_delta_cents: number;
  is_default: boolean;
  incompatible_with: string;
  sort_order: number;
  active: boolean;
}

export interface SignPricing {
  id: number;
  product_id: number | string;
  option_combo_hash: string;
  min_qty: number;
  max_qty: number;
  total_cents: number;
  per_unit_cents: number;
  savings_pct: number;
}

/** LEGACY: v2 catalog does not surface per-option pricing; returns []. Consumers
 *  should migrate to product.variants[] which encode price+availability. */
export async function getSignOptions(_productId: number | string): Promise<SignOption[]> {
  return [];
}

/** LEGACY: v2 pricing is computed server-side; returns []. */
export async function getSignPricing(_productId: number | string): Promise<SignPricing[]> {
  return [];
}

/** LEGACY: v2 catalog is provisioned server-side via blueprint-sync; this is a no-op. */
export async function importProduct(_data: any): Promise<{ productId: string }> {
  // eslint-disable-next-line no-console
  console.warn('[signShopCatalog] importProduct is a no-op on v2. Use admin blueprint-sync instead.');
  return { productId: '' };
}

/** LEGACY: no-op setter for pricing tiers. v2 pricing is computed server-side. */
export async function setSignPricing(_productId: number | string, _tiers: any[]): Promise<void> {
  // eslint-disable-next-line no-console
  console.warn('[signShopCatalog] setSignPricing is a no-op on v2. Use admin pricing-rules instead.');
}

export function calculatePrice(
  basePrice: number,
  selectedOptions: SignOption[],
  pricingTiers: SignPricing[],
  quantity: number,
): { perUnit: number; total: number; savings: number } {
  const optionDelta = selectedOptions.reduce((s, o) => s + (o.price_delta_cents || 0), 0);
  const adjustedBase = basePrice + optionDelta;
  const tier = pricingTiers
    .filter(t => quantity >= t.min_qty && (t.max_qty === 0 || quantity <= t.max_qty))
    .sort((a, b) => b.min_qty - a.min_qty)[0];
  if (tier) {
    return {
      perUnit: tier.per_unit_cents + optionDelta,
      total: (tier.per_unit_cents + optionDelta) * quantity,
      savings: tier.savings_pct,
    };
  }
  return { perUnit: adjustedBase, total: adjustedBase * quantity, savings: 0 };
}

/**
 * Clamp an (x, y, w, h) rect to the bleed bounds of the canvas.
 * Ensures user cannot drag objects outside the printable area.
 */
export function clampToBleed(
  rect: { left: number; top: number; width: number; height: number },
  bounds: CanvasBounds,
  displayScale: number, // ratio: display px per canvas px
): { left: number; top: number; width: number; height: number } {
  const minLeft = -bounds.bleed_px * displayScale;
  const minTop = -bounds.bleed_px * displayScale;
  const maxRight = (bounds.width_px + bounds.bleed_px) * displayScale;
  const maxBottom = (bounds.height_px + bounds.bleed_px) * displayScale;
  const left = Math.max(minLeft, Math.min(rect.left, maxRight - rect.width));
  const top = Math.max(minTop, Math.min(rect.top, maxBottom - rect.height));
  return { left, top, width: rect.width, height: rect.height };
}
